// Klantprojecten: de gezondheid per klantplan (lib/health.ts), zoals het ontwerp (proto-klanten-logic.js). Links de
// klanten met status, voortgang tegenover tijd, einde en te laat; rechts waarom, de cijfers, fases, doelen, mijlpalen en
// wie aan de open taken werkt. Volgt de klantkeuze bovenaan. Einde verschuiven, datums zetten en een plan afsluiten
// gebeurt in Planner zelf: de Project Scheduling API laat Finish en StateCode van een project niet wijzigen.
// Uren komen uit het Clockify-project van het klantplan (Koppelingen): gelogd op taken met een schatting tegenover die
// schatting, alles samen en waarvan support; "–" zolang het plan niet gekoppeld is. Het team toont de bezetting volgende
// week, Support de gesprekken uit de supportmailbox. Medewerker: alleen bekijken.
// Fases staan als boom in de volgorde van Planner, met "x van y af" per laag; een fase opent de faselade (in de schil),
// een taak daaruit de taaklade hier.
import { useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { fmtHours, useCapacity, useClockifyLive, type CapPerson, type PlanHours } from "../lib/api-clockify";
import { useSupport, type SupportOverview } from "../lib/api-support";
import { TaskDrawer } from "../components/TaskDrawer";
import { Av } from "../components/Avatar";
import { catOf } from "../lib/board";
import { allHealth, bezetting, HEALTH_RANK, thresholds, type HealthAction, type HealthStatus, type KlantHealth, type Thresholds } from "../lib/health";
import { useIndex, useMe, usePlanning } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { ddmm, plannerPlanUrl } from "../lib/planning";
import { rightsOf, useSettings } from "../lib/settings";
import { devLabel, hoursSince, waitText } from "../lib/support";
import type { Fase, Me, PlanningData, Person } from "../lib/types";
import { useAppState } from "../shell/AppState";
import { LoadError } from "../shell/Layout";

type Sort = "risico" | "naam" | "einde";
const SORTS: Record<Sort, (a: KlantHealth, b: KlantHealth) => number> = {
  risico: (a, b) => a.rank - b.rank || b.over - a.over || a.klant.name.localeCompare(b.klant.name),
  naam: (a, b) => a.klant.name.localeCompare(b.klant.name),
  einde: (a, b) => a.klant.finish.localeCompare(b.klant.finish) || a.klant.name.localeCompare(b.klant.name),
};
const SORT_TEXT: Record<Sort, string> = { risico: "gesorteerd op risico", naam: "op naam", einde: "op einddatum" };
const nOf = (n: number, one: string, many: string) => n + " " + (n === 1 ? one : many);

/** Wat de planner in Planner zelf doet, per actie uit de gezondheid; bij "look" is er niets te doen. */
const IN_PLANNER: Partial<Record<HealthAction, string>> = {
  shift: "Einde verschuiven doet u in Planner: de app kan de einddatum van een plan niet wijzigen.",
  dates: "Datums zetten doet u in Planner: de app kan de looptijd van een plan niet wijzigen.",
  close: "Het plan afsluiten doet u in Planner. De open taken sluit u af in Opschonen.",
};

const Pill = ({ h }: { h: KlantHealth }) => <span className={"ex-pill ex-pill--" + h.tone}><span className="ex-dot" />{h.status}</span>;

function Bar({ h, big = false }: { h: KlantHealth; big?: boolean }) {
  return <span className={"p-kbar" + (big ? " big" : "")}><i style={{ width: h.donePct + "%" }} /><em style={{ left: h.timePct + "%" }} /></span>;
}

/** Waar de uren van een klantplan vandaan komen, voluit (tooltip en lade). */
function hoursWhy(u: PlanHours): string {
  const other = u.logged - u.loggedOnEstimated;
  return [
    u.estimate ? fmtHours(u.loggedOnEstimated) + " van " + fmtHours(u.estimate) + " geschat, nog " + fmtHours(Math.max(0, u.estimate - u.loggedOnEstimated)) + " te gaan" : fmtHours(u.logged) + " gelogd, geen schatting in Clockify",
    u.estimate && other >= 0.05 ? fmtHours(other) + " op taken zonder schatting" : "",
    u.support >= 0.05 ? fmtHours(u.support) + " support" : "",
  ].filter(Boolean).join(" · ");
}

/** hrs: undefined zonder Clockify (geen kolom), null als het plan niet gekoppeld is ("–"). */
function Row({ h, on, onPick, hrs }: { h: KlantHealth; on: boolean; onPick: () => void; hrs?: PlanHours | null }) {
  const k = h.klant, span = k.start !== k.finish;
  const endSub = !span ? "" : h.left === 0 ? "vandaag" : h.left < 0 ? -h.left + " d voorbij" : "over " + h.left + " d";
  return (
    <button type="button" className={"p-krow" + (on ? " on" : "")} aria-current={on || undefined} onClick={onPick}>
      <span className="p-kname"><b>{k.name}</b><Pill h={h} /></span>
      <span className="p-kprog">{span ? <><Bar h={h} /><small>{h.donePct} % klaar · {h.timePct} % tijd</small></> : <small>geen looptijd</small>}</span>
      <span className={"p-kend" + (span && h.left < 0 ? " t-err" : "")}><b>{span ? ddmm(k.finish) : "–"}</b><small>{endSub}</small></span>
      <span className={"p-klate" + (h.over ? " t-err" : "")}>{h.over || "–"}</span>
      {hrs !== undefined && (
        <span className="p-khrs" title={hrs ? hoursWhy(hrs) : "Niet gekoppeld aan een Clockify-project"}>
          {!hrs ? <span className="t-mute">–</span>
            : hrs.estimate ? <span><b className={hrs.loggedOnEstimated > hrs.estimate ? "t-err" : ""}>{fmtHours(hrs.loggedOnEstimated).replace(" u", "")}</b> / {fmtHours(hrs.estimate)}</span>
            : <><b>{fmtHours(hrs.logged)}</b><small>geen schatting</small></>}
        </span>
      )}
    </button>
  );
}

interface Extra {
  /** undefined zonder Clockify, null als het plan niet gekoppeld is */
  hrs?: PlanHours | null;
  /** capaciteit per persoon, om de bezetting volgende week bij het team te tonen */
  cap: Map<string, CapPerson> | null;
  th: Thresholds;
  support: SupportOverview | null;
}

/** Fases als boom in de volgorde van Planner. Een fase opent de faselade; het pijltje klapt de subfases in. */
function FaseTree({ fases, today, onOpen }: { fases: Fase[]; today: string; onOpen: (id: string) => void }) {
  const [shut, setShut] = useState<Record<string, boolean>>({});
  const ids = new Set(fases.map((f) => f.id));
  const kids = (pid: string | null) => fases.filter((f) => (pid ? f.parentId === pid : !f.parentId || !ids.has(f.parentId)))
    .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
  const rows: ReactNode[] = [];
  const walk = (pid: string | null, lvl: number, seen: Set<string>) => {
    for (const f of kids(pid)) {
      if (seen.has(f.id)) continue; // een lus in de data
      const has = kids(f.id).length > 0, closed = !!shut[f.id];
      const p = f.total ? Math.round((f.done / f.total) * 100) : 0, late = f.end < today && f.done < f.total;
      rows.push(
        <div key={f.id} className="p-kft" style={{ paddingLeft: Math.min(lvl, 4) * 18 }}>
          {has
            ? <button type="button" className="p-kft-tog" aria-expanded={!closed} aria-label={(closed ? "Openklappen: " : "Inklappen: ") + f.title} onClick={() => setShut({ ...shut, [f.id]: !closed })}><Icon name={closed ? "right" : "down"} size={14} /></button>
            : <span className="p-kft-tog" />}
          <button type="button" className="p-kfase p-kft-row" title="Fase openen" onClick={() => onOpen(f.id)}>
            <span className="t"><b title={f.title}>{f.title}</b><small>{f.done} van {f.total} af · {ddmm(f.start)} tot {ddmm(f.end)}</small></span>
            <span className={"p-load " + (p >= 100 ? "ok" : late ? "err" : "low")}><i style={{ width: Math.min(100, p) + "%" }} /></span>
            <b className={late ? "t-err" : p >= 100 ? "t-ok" : ""}>{p} %</b>
          </button>
        </div>,
      );
      if (has && !closed) walk(f.id, lvl + 1, new Set([...seen, f.id]));
    }
  };
  walk(null, 0, new Set());
  return <div className="p-kfases">{rows}</div>;
}

function Detail({ h, data, me, people, onBoard, onOpschonen, onFase, x }: { h: KlantHealth; data: PlanningData; me: Me; people: Map<string, Person>; onBoard: () => void; onOpschonen: () => void; onFase: (id: string) => void; x: Extra }) {
  const planner = me.role === "planner";
  const k = h.klant, span = k.start !== k.finish, today = data.today;
  const open = data.tasks.filter((t) => t.projectId === k.id);
  const critLate = open.filter((t) => catOf(t) === "actief" && t.critical && t.end < today).length;
  const fases = data.fases.filter((f) => f.projectId === k.id);
  const goals = data.goals.filter((g) => g.projectId === k.id).sort((a, b) => a.name.localeCompare(b.name));
  const ms = data.milestones.filter((m) => m.projectId === k.id);
  const msMissed = ms.length > 0 && ms.every((m) => !m.done && m.end < today);
  // team: wie eigenaar is van een open taak in dit plan, de drukste eerst
  const perPerson = new Map<string, number>();
  for (const t of open) for (const o of t.owners) perPerson.set(o.personId, (perPerson.get(o.personId) || 0) + 1);
  const team = [...perPerson].flatMap(([id, n]) => { const p = people.get(id); return p ? [{ p, n }] : []; }).sort((a, b) => b.n - a.n || a.p.name.localeCompare(b.p.name));
  const url = plannerPlanUrl(me, k.id);
  const how = planner ? IN_PLANNER[h.action] : undefined;
  const u = x.hrs;
  const kpis = [
    { label: "Klaar", value: h.donePct + " %", sub: k.done + " van " + nOf(k.total, "taak", "taken") + " afgewerkt", cls: "" },
    { label: "Tijd verstreken", value: span ? h.timePct + " %" : "–", sub: span ? ddmm(k.start) + " tot " + ddmm(k.finish) : "geen looptijd", cls: span && h.timePct - h.donePct >= 40 ? "t-err" : "" },
    ...(u === undefined ? [] : [{ label: "Uren", value: u ? fmtHours(u.logged) : "–", sub: !u ? "niet gekoppeld aan Clockify" : u.estimate ? "gelogd · " + fmtHours(u.estimate) + " geschat" : "gelogd · nog geen schatting", cls: "" }]),
    { label: "Te laat", value: String(h.over), sub: critLate ? nOf(critLate, "kritische taak", "kritische taken") + " erbij" : "actieve taken over deadline", cls: h.over ? "t-err" : "" },
  ];
  // support uit de mailbox: gesprekken van de laatste 12 weken en wat nu open staat
  const sup = x.support, supRow = sup?.klanten.find((r) => r.projectId === k.id);
  const supOpen = (sup?.open || []).filter((c) => c.klantId === k.id).sort((a, b) => a.openSince.localeCompare(b.openSince));

  return (
    <>
      <div className="p-kd-h">
        <div className="p-kd-t"><div style={{ minWidth: 0 }}><p className="ex-eyebrow" style={{ margin: 0 }}>{k.plan}</p><h2>{k.name}</h2></div><Pill h={h} /></div>
        <p className="p-kd-why">{h.why}</p>
        <div className="p-kd-acts">
          {how && <a className="ex-btn ex-btn--primary" href={url} target="_blank" rel="noreferrer"><Icon name="ext" size={15} />Openen in Planner</a>}
          <button type="button" className="ex-btn" onClick={onBoard}><Icon name="board" size={15} />Op het bord</button>
          {planner && <button type="button" className="ex-btn" onClick={onOpschonen}><Icon name="opschonen" size={15} />Opschonen</button>}
          {!how && <a className="ex-icon-btn ex-icon-btn--square p-kd-ext" href={url} target="_blank" rel="noreferrer" title="Openen in Planner" aria-label="Openen in Planner"><Icon name="ext" size={16} /></a>}
        </div>
        {how && <p className="p-kd-note">{how}</p>}
      </div>
      <div className="p-kd-b">
        <div className="p-kpis4" style={kpis.length === 3 ? { gridTemplateColumns: "repeat(3,minmax(0,1fr))" } : undefined}>
          {kpis.map((q) => <div key={q.label} className="p-kq"><span>{q.label}</span><b className={q.cls}>{q.value}</b><small>{q.sub}</small></div>)}
        </div>
        <div className="p-fs">
          <h3>Voortgang tegenover tijd</h3>
          {span && <div className="p-kbig"><Bar h={h} big /><div className="p-kbig-l"><span>{ddmm(k.start)}</span><span>{ddmm(k.finish)}</span></div></div>}
          <p className="p-kd-note">{span ? h.donePct + " % klaar tegenover " + h.timePct + " % van de tijd" : "Zonder looptijd is er geen vergelijking met de tijd."}</p>
          {u && <>
            <div className="p-kd-hours" title={"Clockify-project " + u.name + (u.client ? " (" + u.client + ")" : "")}>
              <span>Uren</span>
              <span className={"p-load " + (u.estimate && u.loggedOnEstimated > u.estimate ? "err" : "ok")}><i style={{ width: (u.estimate ? Math.min(100, Math.round((u.loggedOnEstimated / u.estimate) * 100)) : 0) + "%" }} /></span>
            </div>
            <p className="p-kd-note">{hoursWhy(u)}</p>
          </>}
          {u === null && <p className="p-kd-note">Uren: dit klantplan is nog niet gekoppeld aan een Clockify-project.{planner && <> <Link className="ex-link p-kd-link" to="/koppelingen">Koppelen</Link></>}</p>}
          <div className="p-kd-counts"><span><b>{h.actief}</b>Actief</span><span><b>{h.geparkeerd}</b>Geparkeerd</span><span><b>{h.afsluiten}</b>Klaar om af te sluiten</span></div>
        </div>
        {fases.length > 0 && (
          <div className="p-fs">
            <h3>Fases · {fases.length}</h3>
            <FaseTree fases={fases} today={today} onOpen={onFase} />
          </div>
        )}
        {goals.length > 0 && (
          <div className="p-fs">
            <h3>{goals.length === 1 ? "Doel" : "Doelen · " + goals.length}</h3>
            <div className="p-kfases">
              {goals.map((g) => {
                const p = g.total ? Math.round((g.done / g.total) * 100) : 0;
                return (
                  <div key={g.id} className="p-kfase">
                    <span className="t"><b title={g.name}>{g.name}</b><small>{g.total ? g.done + " van " + g.total + " gekoppelde taken klaar" : "nog geen gekoppelde taken"}</small></span>
                    <span className="p-load low"><i style={{ width: p + "%" }} /></span>
                    <b>{g.total ? p + " %" : "–"}</b>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {ms.length > 0 && (
          <div className="p-fs">
            <h3>Mijlpalen · {ms.length}</h3>
            {ms.map((m) => {
              const late = !m.done && m.end < today;
              return <div key={m.id} className="p-kv"><span className="p-kms"><Icon name="diamond" size={13} />{m.title} · {ddmm(m.end)}</span><b className={m.done ? "t-ok" : late ? "t-err" : "t-mute"}>{m.done ? "gehaald" : late ? "niet gehaald" : "gepland"}</b></div>;
            })}
            {msMissed && <p className="p-kd-note t-warn">Geen enkele mijlpaal is afgevinkt. Controleer of de datums nog kloppen.</p>}
          </div>
        )}
        {team.length > 0 && (
          <div className="p-fs">
            <h3>Team · {team.length}</h3>
            <div className="p-kteam">
              {team.map(({ p, n }) => {
                const c = x.cap?.get(p.id), b = c ? bezetting(c, 1, x.th) : null;
                return <div key={p.id} className="p-kmem"><Av id={p.id} init={p.initials} cls="p-av s" /><span className="n"><b>{p.name}</b><small>{nOf(n, "open taak", "open taken")} in dit plan</small></span>{b && <b className={b.txtCls}>{b.text}</b>}</div>;
              })}
            </div>
            {x.cap && <p className="p-kd-note">Het percentage is de bezetting volgende week, over alle klanten.</p>}
          </div>
        )}
        {sup && (
          <div className="p-fs">
            <h3>Support</h3>
            <p className="p-kd-note" style={{ marginTop: 0 }}>{supRow?.total ? nOf(supRow.total, "supportgesprek", "supportgesprekken") + " in de laatste 12 weken" : "Geen supportgesprekken in de laatste 12 weken"}{supOpen.length ? " · " + supOpen.length + " open" : ""}</p>
            {supOpen.slice(0, 5).map((c) => (
              <div key={c.id} className="p-kv"><span title={c.subject}>{c.subject}</span><b className={c.devs.length ? "" : "t-warn"}>{(c.devs.length ? "bij " + c.devs.map(devLabel).join(" en ") : "nog zonder developer") + " · wacht " + waitText(hoursSince(c.openSince))}</b></div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export function Klantprojecten() {
  const { data, error, refetch } = usePlanning();
  const { data: me } = useMe();
  const idx = useIndex(data);
  const { klant, setKlant } = useAppState();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [sort, setSort] = useState<Sort>("risico");
  const [filter, setFilter] = useState<HealthStatus | null>(null);
  const { data: liveData } = useClockifyLive();
  const { data: settings } = useSettings();
  const mayCap = me?.role === "planner" || rightsOf(settings?.settings).seeCapacity;
  const { data: cap } = useCapacity(!!me && mayCap);
  const { data: supData } = useSupport(null);
  if (!data && error) return <LoadError what="De planningsdata" onRetry={() => refetch()} />;
  if (!data || !me) return <div className="p-loading">Planningsdata laden…</div>;

  // Clockify en de supportmailbox: zonder koppeling geen kolom Uren en geen supportblok
  const live = liveData && liveData.clockify.state !== "uit" ? liveData : null;
  const hrsOf = (id: string) => (live ? live.plans[id] || null : undefined);
  const extra = (id: string): Extra => ({
    hrs: hrsOf(id), th: thresholds(settings?.settings),
    cap: mayCap && cap ? new Map(cap.people.map((c) => [c.personId, c])) : null,
    support: supData && supData.sync.state !== "uit" && supData.sync.lastOk ? supData : null,
  });

  const all = allHealth(data);
  const kl = klant ? idx.klanten.get(klant) : undefined;
  const counts = new Map<HealthStatus, number>();
  for (const h of all) counts.set(h.status, (counts.get(h.status) || 0) + 1);
  const statuses = (Object.keys(HEALTH_RANK) as HealthStatus[]).filter((s) => counts.get(s));
  const tone = (s: HealthStatus) => all.find((h) => h.status === s)?.tone || "off";
  const list = all.filter((h) => (!filter || h.status === filter) && (!kl || h.klant.id === kl.id)).sort(SORTS[sort]);
  const sel = params.get("plan");
  const cur = list.find((h) => h.klant.id === sel) || list[0] || null;
  const pick = (id: string) => setParams((p) => { p.set("plan", id); return p; }, { replace: true });
  // naar een ander scherm met deze klant gekozen bovenaan
  const go = (to: string) => { if (cur) setKlant(cur.klant.id); navigate(to); };
  const openFase = (id: string) => setParams((p) => { p.delete("taak"); p.set("fase", id); return p; });
  const taak = params.get("taak"), selTask = taak ? data.tasks.find((t) => t.id === taak) : null;
  const closeTask = () => setParams((p) => { p.delete("taak"); return p; });

  return (
    <>
      <div className="p-head">
        <div><h1>Klantprojecten</h1><p>{nOf(data.klanten.length, "plan", "plannen")} · {SORT_TEXT[sort]} · voortgang is het deel afgewerkte taken, de streep is de verstreken tijd</p></div>
        <div className="p-tools">
          <div className="ex-seg" role="group" aria-label="Sorteren">
            <button type="button" aria-pressed={sort === "risico"} onClick={() => setSort("risico")}>Op risico</button>
            <button type="button" aria-pressed={sort === "naam"} onClick={() => setSort("naam")}>Op naam</button>
            <button type="button" aria-pressed={sort === "einde"} onClick={() => setSort("einde")}>Op einde</button>
          </div>
        </div>
      </div>
      <div className="p-kchips">
        {statuses.map((s) => (
          <button key={s} type="button" className={"p-kchip " + tone(s) + (filter === s ? " on" : "")} aria-pressed={filter === s} onClick={() => setFilter(filter === s ? null : s)}><i />{s} · {counts.get(s)}</button>
        ))}
        {(filter || kl) && <button type="button" className="ex-link" onClick={() => { setFilter(null); setKlant(null); }}>Alle klanten tonen</button>}
      </div>
      <div className={"p-kl" + (live ? " hrs" : "")}>
        <section className="p-card p-kl-list">
          <div className="p-klh"><span>Klant</span><span>Voortgang tegenover tijd</span><span>Einde</span><span>Te laat</span>{live && <span>Uren</span>}</div>
          <div className="p-kl-body">
            {list.map((h) => <Row key={h.klant.id} h={h} on={cur?.klant.id === h.klant.id} onPick={() => pick(h.klant.id)} hrs={hrsOf(h.klant.id)} />)}
            {!list.length && <p className="p-empty p-mw-empty">{data.klanten.length ? "Geen klanten met deze status." : "Er zijn nog geen klantplannen."}</p>}
          </div>
        </section>
        <section className="p-card p-kd">
          {cur
            ? <Detail key={cur.klant.id} h={cur} data={data} me={me} people={idx.people} onBoard={() => go("/planning")} onOpschonen={() => go("/opschonen")} onFase={openFase} x={extra(cur.klant.id)} />
            : <p className="p-empty p-mw-empty">Geen klant om te tonen.</p>}
        </section>
      </div>
      {selTask && <TaskDrawer key={selTask.id} task={selTask} data={data} onClose={closeTask} />}
    </>
  );
}
